export function buildCompactChecklistSummaryGroups(result, reviewState) {
    const groups = []
    const filesById = new Map((result?.files || []).map(file => [file.id, file]))
    let checklistNumber = 0

    for (const section of result?.sections || []) {
        for (const item of section.items || []) {
            checklistNumber += 1
            const status = reviewState?.[item.id]?.finalStatus || item.finalStatus || item.suggestedStatus
            if (!['warning', 'fail'].includes(status)) continue

            groups.push({
                itemId: item.id || `${section.key}-${checklistNumber}`,
                checklistNumber,
                label: item.label || 'Checklist item',
                files: buildChecklistLocationFiles(item, filesById).map(({ sourceLocations, ...file }) => file)
            })
        }
    }

    return groups
}

export function buildCompactSummaryAlerts(result) {
    const alerts = []
    const seen = new Set()

    for (const fileResult of result?.fileResults || []) {
        const fileName = fileResult.name || 'Review file'
        for (const alert of fileResult.alerts || []) {
            const type = String(alert.type || 'system_alert')
            const title = String(alert.title || blockerTitle(type)).trim()
            const key = `${fileName}:${type}`.toLowerCase()
            if (seen.has(key)) continue
            seen.add(key)
            alerts.push({ fileName, title })
        }
    }

    return alerts
}

export function buildBlockingReviewAlerts(result, reviewState = {}) {
    const alerts = []
    const seen = new Set()
    const filesById = new Map((result?.files || []).map(file => [file.id, file]))

    for (const alert of result?.blockers || []) {
        const fileName = alert.fileName || filesById.get(alert.fileId)?.name || 'Review batch'
        const type = alert.type || 'system_alert'
        const identityKeys = blockerIdentityKeys(alert, alert.fileId, fileName, type)
        if (identityKeys.some(key => seen.has(key))) continue
        identityKeys.forEach(key => seen.add(key))
        const fallbackId = `${alert.fileId || fileName}:${type}`.toLowerCase()
        alerts.push(buildBlockerViewModel({
            id: alert.id || fallbackId,
            stateKey: alert.stateKey || `alert:${alert.id || fallbackId}`,
            fileId: alert.fileId || '',
            fileName,
            type,
            severity: alert.severity || 'warning',
            blocksReview: alert.blocksReview !== false && (alert.blocking !== false || alert.blocksReview === true),
            title: alert.title || blockerTitle(type),
            summary: alert.summary || '',
            details: Array.isArray(alert.details) ? alert.details : [],
            locations: Array.isArray(alert.locations) ? alert.locations.map(formatStructuredLocation).filter(Boolean) : [],
            resolution: alert.resolution,
            reviewerNote: alert.reviewerNote,
            resolved: alert.resolved
        }, reviewState))
    }

    for (const fileResult of result?.fileResults || []) {
        for (const alert of fileResult.alerts || []) {
            const type = String(alert.type || 'system_alert')
            const severity = String(alert.severity || 'warning')
            const fileName = alert.fileName || fileResult.name || filesById.get(fileResult.fileId)?.name || 'Review file'
            const fileId = alert.fileId || fileResult.fileId || ''
            const blocksReview = alert.blocksReview === true || type === 'wrong_client' || severity === 'error' || alert.blockAutoFix === true
            if (!blocksReview) continue
            const identityKeys = blockerIdentityKeys(alert, fileId, fileName, type)
            if (identityKeys.some(key => seen.has(key))) continue
            identityKeys.forEach(key => seen.add(key))
            const id = alert.id || `${fileId || fileName}:${type}`.toLowerCase()
            alerts.push(buildBlockerViewModel({
                id,
                stateKey: alert.stateKey || `alert:${id}`,
                fileId,
                fileName,
                type,
                severity,
                blocksReview,
                title: alert.title || blockerTitle(type),
                summary: alert.summary || '',
                details: Array.isArray(alert.details) ? alert.details : [],
                locations: Array.isArray(alert.locations) ? alert.locations.map(formatStructuredLocation).filter(Boolean) : [],
                resolution: alert.resolution,
                reviewerNote: alert.reviewerNote,
                resolved: alert.resolved
            }, reviewState))
        }
    }

    for (const file of result?.files || []) {
        if (file?.supported !== false) continue
        if (alerts.some(alert =>
            alert.fileId === file.id && ['unreadable_file', 'unsupported'].includes(alert.type)
        )) continue
        const identityKeys = blockerIdentityKeys({}, file.id, file.name, 'unreadable_file')
        if (identityKeys.some(key => seen.has(key))) continue
        identityKeys.forEach(key => seen.add(key))
        const id = `unreadable-file:${file.id || file.name}`
        alerts.push(buildBlockerViewModel({
            id,
            stateKey: `alert:${id}`,
            fileId: file.id || '',
            fileName: file.name || 'Review file',
            type: 'unreadable_file',
            severity: 'error',
            blocksReview: true,
            title: 'File could not be checked',
            summary: file.parseMessage || 'The system could not read this file.',
            details: [],
            locations: []
        }, reviewState))
    }

    return alerts.sort((left, right) => Number(right.blocking) - Number(left.blocking))
}

export function buildClientReadyActionItems(result, reviewState, options = {}) {
    const output = []
    const filesById = new Map((result?.files || []).map(file => [file.id, file]))
    const structuredLocationsByItem = buildStructuredLocationsByItem(result)
    const expectations = options.expectations || {}
    let checklistNumber = 0

    for (const section of result?.sections || []) {
        for (const item of section.items || []) {
            checklistNumber += 1
            const suggestedStatus = item.suggestedStatus || item.finalStatus || 'manual'
            const state = reviewState?.[item.id] || {}
            const finalStatus = state.finalStatus || item.finalStatus || suggestedStatus
            const resolution = normalizeResolution(state.resolution, finalStatus, suggestedStatus)
            const startsActionable = ['warning', 'fail', 'manual'].includes(suggestedStatus)
            const wasResolved = ['fixed', 'not_applicable'].includes(resolution)
            if (!startsActionable && !wasResolved) continue

            const formalLabel = item.label || 'Checklist item'
            output.push({
                id: item.id || `${section.key}-${checklistNumber}`,
                stateKeys: [item.id || `${section.key}-${checklistNumber}`],
                checklistNumber,
                sectionKey: section.key || '',
                sectionTitle: section.title || '',
                label: buildConciseDisplayLabel(formalLabel),
                formalLabel,
                summary: item.summary || '',
                suggestedStatus,
                finalStatus,
                resolution,
                note: String(state.note || ''),
                kind: suggestedStatus === 'manual' ? 'manual' : 'action',
                files: buildChecklistLocationFiles(item, filesById, structuredLocationsByItem.get(item.id)),
                evidence: buildChecklistEvidence(item.id, expectations),
                details: Array.isArray(item.details) ? item.details : []
            })
        }
    }

    return output
}

export function buildWordingActionItems(result, reviewState) {
    const groups = new Map()

    for (const file of result?.wordingReview?.files || []) {
        for (const finding of file.findings || []) {
            const formalLabel = finding.title || 'Wording issue'
            const ruleKey = normalizeWordingRule(
                finding.ruleId || finding.ruleKey || finding.rule || finding.code || formalLabel
            )
            const groupKey = `${file.fileId || file.fileName || 'file'}:${ruleKey}`
            const stateKey = finding.reviewStateKey || `wording:${file.fileId || ''}:${finding.id || formalLabel}`
            const state = reviewState?.[stateKey] || {}
            const resolution = normalizeResolution(
                state.resolution || finding.resolution,
                state.finalStatus || finding.finalStatus || 'warning',
                'warning'
            )

            if (!groups.has(groupKey)) {
                groups.set(groupKey, {
                    id: `wording-group:${groupKey}`,
                    stateKeys: [],
                    checklistNumber: '',
                    sectionKey: 'wording_review',
                    sectionTitle: 'Wording review',
                    label: buildConciseDisplayLabel(formalLabel),
                    formalLabel,
                    summary: finding.issue || '',
                    suggestedStatus: 'warning',
                    finalStatus: 'warning',
                    resolutions: [],
                    notes: [],
                    kind: 'wording',
                    files: [{
                        fileId: file.fileId || '',
                        fileName: file.fileName || 'Review file',
                        locations: [],
                        sourceLocations: []
                    }],
                    occurrences: [],
                    details: []
                })
            }

            const group = groups.get(groupKey)
            group.stateKeys.push(stateKey)
            group.resolutions.push(resolution)
            if (state.note || finding.reviewerNote) group.notes.push(String(state.note || finding.reviewerNote))
            const findingLocations = wordingFindingLocations(finding)
            group.files[0].locations.push(...findingLocations)
            const structuredSourceLocations = (finding.locations || []).map(location =>
                buildSourceLocationDescriptor(location, formatStructuredLocation(location))
            )
            group.files[0].sourceLocations.push(...structuredSourceLocations)
            if (!structuredSourceLocations.length) {
                group.files[0].sourceLocations.push(...findingLocations.map(location =>
                    buildSourceLocationDescriptor(null, location)
                ))
            }
            const occurrence = {
                stateKey,
                location: findingLocations[0] || '',
                currentText: finding.originalText || finding.snippet || '',
                suggestedText: finding.proposedText || finding.suggestion || ''
            }
            if (occurrence.location || occurrence.currentText || occurrence.suggestedText) {
                group.occurrences.push(occurrence)
            }
            if (finding.rationale) group.details.push(finding.rationale)
        }
    }

    return Array.from(groups.values()).map(group => {
        const resolution = combineGroupedResolutions(group.resolutions)
        const note = uniqueStrings(group.notes).join(' · ')
        const locations = uniqueStrings(group.files[0].locations)
        return {
            ...group,
            stateKeys: uniqueStrings(group.stateKeys),
            resolution,
            finalStatus: resolution === 'fixed' ? 'pass' : resolution === 'not_applicable' ? 'na' : 'warning',
            note,
            files: [{
                ...group.files[0],
                locations,
                sourceLocations: uniqueSourceLocationDescriptors(group.files[0].sourceLocations)
            }],
            evidence: group.occurrences.length === 1
                ? {
                    currentText: group.occurrences[0].currentText,
                    expectedText: group.occurrences[0].suggestedText,
                    highlightTexts: [group.occurrences[0].currentText].filter(Boolean)
                }
                : null,
            currentText: group.occurrences.length === 1 ? group.occurrences[0].currentText : '',
            suggestedText: group.occurrences.length === 1 ? group.occurrences[0].suggestedText : '',
            details: uniqueStrings(group.details),
            resolutions: undefined,
            notes: undefined
        }
    })
}

export function buildReviewOutcome(result, reviewState) {
    const actions = [
        ...buildClientReadyActionItems(result, reviewState),
        ...buildWordingActionItems(result, reviewState)
    ]
    const blockers = buildBlockingReviewAlerts(result, reviewState)
    const checklistItems = (result?.sections || []).flatMap(section => section.items || [])

    return {
        open: actions.filter(item => item.resolution === 'open' && item.kind !== 'manual').length,
        manual: actions.filter(item => item.resolution === 'open' && item.kind === 'manual').length,
        fixed: actions.filter(item => item.resolution === 'fixed').length,
        notApplicable: actions.filter(item => item.resolution === 'not_applicable').length,
        reviewed: actions.filter(item => item.resolution === 'reviewed').length,
        needsClarification: actions.filter(item => item.resolution === 'needs_clarification').length,
        blocking: blockers.filter(alert => alert.blocking).length,
        passed: checklistItems.filter(item => {
            const state = reviewState?.[item.id] || {}
            return (state.finalStatus || item.finalStatus || item.suggestedStatus) === 'pass' && !state.resolution
        }).length,
        totalActions: actions.length
    }
}

export function buildReviewCoverage(result, reviewState) {
    const actions = [
        ...buildClientReadyActionItems(result, reviewState),
        ...buildWordingActionItems(result, reviewState)
    ]
    const files = result?.files || []
    const unreadableFileIds = new Set()
    for (const file of files) {
        if (!file.supported || (file.parseStatus && file.parseStatus !== 'parsed')) {
            unreadableFileIds.add(file.id || file.name)
        }
    }
    for (const fileResult of result?.fileResults || []) {
        if ((fileResult.alerts || []).some(alert => ['unreadable_file', 'unsupported'].includes(alert.type))) {
            unreadableFileIds.add(fileResult.fileId || fileResult.name)
        }
    }
    for (const skipped of result?.skippedFiles || []) {
        unreadableFileIds.add(skipped.sourceFileId || skipped.id || skipped.name)
    }

    const locationQualities = actions.map(item => actionLocationQuality(item))
    return {
        filesTotal: files.length + (result?.skippedFiles || []).filter(skipped =>
            !files.some(file => (file.id || file.name) === (skipped.sourceFileId || skipped.id || skipped.name))
        ).length,
        filesRead: files.filter(file => file.supported && (!file.parseStatus || file.parseStatus === 'parsed')).length,
        filesNotFullyChecked: unreadableFileIds.size,
        checksEvaluated: (result?.sections || []).flatMap(section => section.items || []).length,
        issuesFound: actions.filter(item => item.kind !== 'manual').length,
        manualChecks: actions.filter(item => item.kind === 'manual' && isUnresolvedResolution(item.resolution)).length,
        exactLocationItems: locationQualities.filter(quality => quality === 'exact').length,
        pageOnlyLocationItems: locationQualities.filter(quality => quality === 'page').length,
        missingLocationItems: locationQualities.filter(quality => quality === 'missing').length,
        actionableItems: actions.length
    }
}

export function buildReviewCompletionState(result, reviewState) {
    const blockers = buildBlockingReviewAlerts(result, reviewState).filter(alert => alert.blocking)
    const items = [
        ...buildClientReadyActionItems(result, reviewState),
        ...buildWordingActionItems(result, reviewState)
    ]
    const remainingItems = items.filter(item => isUnresolvedResolution(item.resolution))

    if (blockers.length) {
        const blocker = blockers[0]
        return {
            status: 'blocked',
            severity: 'error',
            title: 'Review required before sending to client',
            remaining: remainingItems.length,
            nextAction: `Next: review ${blocker.fileName || 'this file'} — ${blocker.title}`,
            itemId: blocker.stateKey || blocker.id
        }
    }

    if (remainingItems.length) {
        const item = remainingItems[0]
        const file = item.files[0] || {}
        const location = file.locations?.[0] || ''
        const locationSuffix = location ? ` · ${location}` : ''
        return {
            status: 'remaining',
            severity: 'warning',
            title: `${remainingItems.length} item${remainingItems.length === 1 ? '' : 's'} remaining`,
            remaining: remainingItems.length,
            nextAction: `Next: ${item.checklistNumber ? `${item.checklistNumber}. ` : ''}${item.label} · ${file.fileName || 'Review file'}${locationSuffix}`,
            itemId: item.id
        }
    }

    return {
        status: 'ready',
        severity: 'success',
        title: 'Ready to issue',
        remaining: 0,
        nextAction: 'All detected issues and manual checks have been reviewed.',
        itemId: ''
    }
}

function actionLocationQuality(item) {
    const sourceLocations = (item?.files || []).flatMap(file => file.sourceLocations || [])
    if (sourceLocations.some(location =>
        (location.worksheet && location.cellRef) ||
        location.heading || location.noteLabel ||
        (location.paragraphIndex && (location.kind === 'docx_paragraph' || !Number(location.pageNumber || 0)))
    )) return 'exact'
    if (sourceLocations.some(location => Number(location.pageNumber || 0) > 0)) return 'page'
    if ((item?.files || []).some(file => (file.locations || []).some(location => isUsableLocation(location)))) return 'page'
    return 'missing'
}

export function formatClientReadySummaryText(result, reviewState, displayCompanyName = '') {
    const actions = buildClientReadyActionItems(result, reviewState)
        .filter(item => isUnresolvedResolution(item.resolution))
    const wording = buildWordingActionItems(result, reviewState)
        .filter(item => isUnresolvedResolution(item.resolution))
    const blockers = buildBlockingReviewAlerts(result, reviewState).filter(alert => alert.blocking)
    const companyName = displayCompanyName || result?.sourceOfTruth?.expectations?.expectedCompanyName || ''
    const lines = [
        'QUALITY REVIEW FINDINGS',
        companyName,
        result?.createdAt ? `Created ${result.createdAt}` : ''
    ].filter(Boolean)

    for (const alert of blockers) {
        lines.push('', `BLOCKER — ${alert.fileName}`, alert.title)
        lines.push(...(alert.locations || []))
    }

    for (const item of actions) {
        lines.push('', `${item.checklistNumber}. ${item.label}`)
        for (const file of item.files) {
            lines.push(file.fileName)
            lines.push(...file.locations)
        }
    }

    if (wording.length) {
        lines.push('', 'WORDING REVIEW')
        for (const item of wording) {
            lines.push(item.label)
            for (const file of item.files) {
                lines.push(file.fileName)
                lines.push(...file.locations)
            }
        }
    }

    return lines.join('\n').trim()
}

export function extractCompactFindingLocations(detail) {
    const text = stripSummaryFilePrefix(detail)
    if (!text) return []

    const locations = []
    const wherePattern = /Where to look:\s*((?:worksheet\s+"[^"]+"(?:,\s*cell\s+[A-Z]+\d+)?|(?:PDF\s+page|page)\s+\d+(?:\s*\/\s*[^,.]+\s+\d+)?|Word\s+paragraph\s+\d+|page\s+header)(?:,\s*note\s+[A-Za-z0-9().-]+)?)[.]?(?:\s+(?:Currently using|Preview):\s*"([^"]*)")?/gi
    let match

    while ((match = wherePattern.exec(text))) {
        locations.push(formatCompactFindingLocation(match[1], match[2]))
    }

    if (locations.length) return locations.filter(Boolean)

    if (/\bcontents page\b/i.test(text)) {
        return ['Contents page']
    }

    const contextualPagePattern = /\b((?:PDF|FS|LoR)\s+page\s+\d+(?:\s*\/\s*[^,.:;]+\s+\d+)?)(?:,\s*|\s+)?(note\s+[A-Za-z0-9().-]+)?/gi
    while ((match = contextualPagePattern.exec(text))) {
        const location = [match[1], match[2]].filter(Boolean).join(', ')
        locations.push(formatCompactFindingLocation(location))
    }

    const quotedPagePattern = /\b(page\s+\d+(?:\s*\/\s*[^,.:;]+\s+\d+)?)(?:,\s*(note\s+[A-Za-z0-9().-]+))?\s*:\s*"([^"]*)"/gi
    while ((match = quotedPagePattern.exec(text))) {
        const location = [match[1], match[2]].filter(Boolean).join(', ')
        locations.push(formatCompactFindingLocation(location, match[3]))
    }

    if (locations.length) return locations.filter(Boolean)

    const noteParagraphPattern = /\b(note\s+[A-Za-z0-9().-]+(?:,\s*paragraph\s+\d+)|paragraph\s+\d+)\b/gi
    while ((match = noteParagraphPattern.exec(text))) {
        locations.push(capitalizeSentenceStart(match[1]))
    }

    if (locations.length) return locations.filter(Boolean)

    const worksheetPattern = /\b(?:worksheet|sheet)\s+"?([^",:;]+)"?(?:,\s*|\s+)(?:cell\s+([A-Z]+\d+)|row\s+(\d+))/gi
    while ((match = worksheetPattern.exec(text))) {
        locations.push(`${match[2] ? 'Worksheet' : 'Sheet'} ${match[1].trim()}, ${match[2] ? `cell ${match[2]}` : `row ${match[3]}`}`)
    }

    if (locations.length) return locations.filter(Boolean)

    const cellPattern = /\bcell\s+([A-Z]+\d+)\b/gi
    while ((match = cellPattern.exec(text))) {
        locations.push(`Cell ${match[1]}`)
    }

    return locations.filter(Boolean)
}

function extractSummaryFileName(detail, item, filesById) {
    const value = String(detail || '')
    const directFile = (item.applicableFiles || []).find(fileName => value.startsWith(`${fileName}:`))
    if (directFile) return directFile

    const affectedFileId = (item.affectedFileIds || [])[0] || (item.applicableFileIds || [])[0]
    return filesById.get(affectedFileId)?.name || (item.applicableFiles || [])[0] || 'Review file'
}

function buildChecklistLocationFiles(item, filesById, structuredEntries = []) {
    const structuredFiles = (structuredEntries || [])
        .map(entry => {
            const sourceLocations = uniqueSourceLocationDescriptors((entry.locations || [])
                .map(location => buildSourceLocationDescriptor(location, formatStructuredLocation(location))))
            return {
                fileId: entry.fileId,
                fileName: entry.fileName,
                locations: uniqueStrings(sourceLocations.map(location => location.label).filter(Boolean)),
                sourceLocations
            }
        })
        .filter(file => file.locations.length)
    if (structuredFiles.length) return structuredFiles

    const fileMap = new Map()
    const details = (item.details?.length ? item.details : [''])
        .filter(detail => !isPositiveFindingDetail(detail))

    for (const detail of details) {
        const fileName = extractSummaryFileName(detail, item, filesById)
        const fileId = findFileId(fileName, item, filesById)
        if (!fileMap.has(fileName)) {
            fileMap.set(fileName, {
                fileId,
                fileName,
                locations: [],
                sourceLocations: [],
                seen: new Set()
            })
        }

        const fileEntry = fileMap.get(fileName)
        for (const location of extractCompactFindingLocations(detail)) {
            const normalized = location.toLowerCase()
            if (fileEntry.seen.has(normalized)) continue
            fileEntry.seen.add(normalized)
            fileEntry.locations.push(location)
            fileEntry.sourceLocations.push(buildSourceLocationDescriptor(null, location))
        }
    }

    const files = Array.from(fileMap.values()).map(file => ({
        fileId: file.fileId,
        fileName: file.fileName,
        locations: file.locations,
        sourceLocations: uniqueSourceLocationDescriptors(file.sourceLocations)
    }))

    if (!files.length) {
        const fileName = extractSummaryFileName('', item, filesById)
        files.push({
            fileId: findFileId(fileName, item, filesById),
            fileName,
            locations: [],
            sourceLocations: []
        })
    }

    return files
}

function buildStructuredLocationsByItem(result) {
    const output = new Map()

    for (const fileResult of result?.fileResults || []) {
        for (const finding of fileResult.findings || []) {
            if (!finding.itemId || !Array.isArray(finding.locations) || !finding.locations.length) continue
            if (['warning', 'fail'].includes(finding.suggestedStatus) && finding.isAffected === false) continue
            if (!output.has(finding.itemId)) output.set(finding.itemId, [])
            output.get(finding.itemId).push({
                fileId: fileResult.fileId || '',
                fileName: fileResult.name || 'Review file',
                locations: finding.locations
            })
        }
    }

    return output
}

function formatStructuredLocation(location) {
    if (!location || typeof location !== 'object') return String(location || '')
    const snippet = compactLocationPreview(location.snippet || '')

    if (location.kind === 'worksheet_cell' || location.worksheet || location.cellRef) {
        return [
            location.worksheet ? `Worksheet ${location.worksheet}` : '',
            location.cellRef ? `cell ${location.cellRef}` : ''
        ].filter(Boolean).join(', ')
    }
    if (location.pageNumber || location.displayPageNumber) {
        const pageNumber = location.displayPageNumber || location.pageNumber
        const base = `Page ${pageNumber}${location.lineNumber ? `, line ${location.lineNumber}` : ''}`
        if (location.heading) return `${base}, heading`
        if (location.noteLabel) return `${base}, note ${location.noteLabel}`
        return snippet ? `${base}, paragraph beginning “${snippet}”` : base
    }
    if (location.kind === 'docx_paragraph' || location.paragraphIndex) {
        const paragraph = location.paragraphIndex ? `Paragraph ${location.paragraphIndex}` : 'Word paragraph'
        return snippet ? `${paragraph} beginning “${snippet}”` : paragraph
    }

    return snippet
}

function buildSourceLocationDescriptor(location, fallbackLabel = '') {
    const value = location && typeof location === 'object' ? location : {}
    const label = String(fallbackLabel || '').trim()
    const pageMatch = label.match(/\b(?:PDF\s+)?page\s+(\d+)\b/i)
    const pageNumber = Number(value.pageNumber || value.renderedPage || value.inferredPage || pageMatch?.[1] || 0)
    const displayPageNumber = Number(value.displayPageNumber || 0)
    return {
        label,
        pageNumber,
        displayPageNumber,
        paragraphIndex: Number(value.paragraphIndex || 0),
        lineNumber: Number(value.lineNumber || label.match(/\bline\s+(\d+)\b/i)?.[1] || 0),
        heading: String(value.heading || ''),
        noteLabel: String(value.noteLabel || ''),
        worksheet: String(value.worksheet || ''),
        cellRef: String(value.cellRef || ''),
        matchText: String(value.matchText || '').trim().slice(0, 512),
        contextText: String(value.contextText || '').trim().slice(0, 512),
        snippet: compactLocationPreview(value.snippet || '')
    }
}

function uniqueSourceLocationDescriptors(locations) {
    const seen = new Set()
    return (locations || []).filter(location => {
        const key = [
            location.label,
            location.pageNumber,
            location.displayPageNumber,
            location.paragraphIndex,
            location.lineNumber,
            location.heading,
            location.noteLabel,
            location.worksheet,
            location.cellRef,
            location.matchText,
            location.contextText,
            location.snippet
        ].join('|').toLowerCase()
        if (!key.replace(/\|/g, '')) return false
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

function buildChecklistEvidence(itemId, expectations) {
    const id = String(itemId || '').toLowerCase()
    let expectedText = ''

    if (id.includes('company_name')) expectedText = expectations.expectedCompanyName
    else if (id.includes('uen') || id.includes('registration_number')) expectedText = expectations.expectedUen
    else if (id.includes('registered_address')) expectedText = expectations.expectedRegisteredAddress
    else if (id.includes('secretar')) expectedText = expectations.expectedCompanySecretaries
    else if (id.includes('director')) expectedText = expectations.expectedDirectors
    else if (id.includes('country_of_domicile')) expectedText = expectations.expectedCountryOfDomicile
    else if (id.includes('tax_ya') || id.endsWith('_ya')) expectedText = expectations.expectedYa
    else if (id.includes('fye') || id.includes('financial_period') || id.includes('same_date')) {
        expectedText = expectations.expectedFinancialPeriodEnd || expectations.expectedFye
    }

    const highlightTexts = String(expectedText || '').trim().length >= 4
        ? [String(expectedText).trim()]
        : []

    return expectedText
        ? { expectedText: String(expectedText), currentText: '', highlightTexts }
        : null
}

function wordingFindingLocations(finding) {
    const structured = uniqueStrings((finding?.locations || [])
        .map(formatStructuredLocation).filter(isUsableLocation))
    if (structured.length) return structured
    const directLocation = String(finding?.locationHint || '')
        .split(/Currently using:/i)[0].replace(/^Where to look:\s*/i, '').replace(/\.\s*$/, '').trim()
    return isUsableLocation(directLocation) ? [directLocation] : []
}

function isUsableLocation(value) {
    const text = String(value || '').trim()
    return Boolean(text) && !/^location (?:is )?not available$/i.test(text)
}

function blockerIdentityKeys(alert, fileId, fileName, type) {
    const keys = []
    if (alert?.id) keys.push(`id:${String(alert.id).toLowerCase()}`)
    if (alert?.stateKey) keys.push(`state:${String(alert.stateKey).toLowerCase()}`)
    keys.push(`file-type:${String(fileId || fileName || 'review-batch').toLowerCase()}:${String(type || 'system_alert').toLowerCase()}`)
    return uniqueStrings(keys)
}

function blockerTitle(type) {
    const titles = {
        wrong_client: 'Possible wrong-client file',
        unreadable_file: 'File could not be checked',
        unsupported: 'File could not be checked',
        source_unconfirmed: 'BizFile information needs confirmation',
        source_quality: 'BizFile information needs review',
        cross_document_consistency: 'Cross-document details need review',
        fs_consistency: 'Financial statements need a consistency check',
        visual_layout: 'Document layout needs review',
        operations_amendment: 'Operational amendment needs review'
    }
    return titles[type] || 'File needs review'
}

function uniqueStrings(values) {
    return Array.from(new Set((values || []).map(value => String(value || '').trim()).filter(Boolean)))
}

function findFileId(fileName, item, filesById) {
    const matchingEntry = Array.from(filesById.entries()).find(([, file]) => file?.name === fileName)
    if (matchingEntry) return matchingEntry[0]
    return (item.affectedFileIds || [])[0] || (item.applicableFileIds || [])[0] || ''
}

function buildBlockerViewModel(blocker, reviewState) {
    const state = reviewState?.[blocker.stateKey] || reviewState?.[blocker.id] || {}
    const resolution = normalizeResolution(
        state.resolution || blocker.resolution || (blocker.resolved ? 'fixed' : 'open'),
        state.finalStatus || '',
        'warning'
    )
    const resolved = ['fixed', 'not_applicable'].includes(resolution)
    const blocksReview = blocker.blocksReview !== false

    return {
        ...blocker,
        resolution,
        reviewerNote: String(state.note ?? blocker.reviewerNote ?? ''),
        resolved,
        blocksReview,
        blocking: blocksReview && !resolved
    }
}

function buildConciseDisplayLabel(value) {
    const formalLabel = String(value || 'Checklist item').trim()
    const knownLabels = [
        [/^company registration number(?:\s*\(uen\))?$/i, 'Company registration number'],
        [/^registered address is correct$/i, 'Registered address'],
        [/^company name is consistent throughout$/i, 'Company name'],
        [/^directors?'? signatures? (?:are|is) present$/i, 'Directors’ signatures']
    ]
    const known = knownLabels.find(([pattern]) => pattern.test(formalLabel))
    if (known) return known[1]

    return formalLabel
        .replace(/\s+is consistent throughout$/i, '')
        .replace(/\s+(?:is|are) correct$/i, '')
        .replace(/\s+(?:is|are) present$/i, '')
        .trim() || formalLabel
}

function normalizeWordingRule(value) {
    return String(value || 'wording-issue')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'wording-issue'
}

function combineGroupedResolutions(values) {
    const resolutions = uniqueStrings(values)
    if (!resolutions.length || resolutions.includes('open')) return 'open'
    if (resolutions.includes('needs_clarification')) return 'needs_clarification'
    if (resolutions.length === 1) return resolutions[0]
    return 'reviewed'
}

function isUnresolvedResolution(value) {
    return value === 'open' || value === 'needs_clarification'
}

function normalizeResolution(value, finalStatus, suggestedStatus) {
    if (value === 'na') return 'not_applicable'
    if (['open', 'fixed', 'not_applicable', 'needs_clarification'].includes(value)) return value
    if (finalStatus === 'na') return 'not_applicable'
    if (finalStatus === 'pass' && ['warning', 'fail', 'manual'].includes(suggestedStatus)) return 'fixed'
    return 'open'
}

function stripSummaryFilePrefix(detail) {
    const value = String(detail || '').trim()
    const colonIndex = value.indexOf(':')
    if (colonIndex <= 0) return value

    const possiblePrefix = value.slice(0, colonIndex)
    if (/\.(docx?|xlsx?|pdf)$/i.test(possiblePrefix)) {
        return value.slice(colonIndex + 1).trim()
    }

    return value
}

function isPositiveFindingDetail(detail) {
    const value = stripSummaryFilePrefix(detail).toLowerCase()
    if (!value) return false

    const issuePatterns = [
        /\bneeds correction\b/,
        /\bneeds review\b/,
        /\bmissing\b/,
        /\binconsistent\b/,
        /\bmismatch\b/,
        /\bwrong\b/,
        /\bincorrect\b/,
        /\bdiffers?\b/,
        /\bdoes not\b/,
        /\bnot detected\b/,
        /\bno matching\b/,
        /\bissue detected\b/,
        /\bproblem detected\b/,
        /\bflagged\b/,
        /\bplease\s+(?:amend|change|correct|insert|review|align|update|normalise|normalize|hide|move|add)\b/
    ]
    if (issuePatterns.some(pattern => pattern.test(value))) return false

    const positivePatterns = [
        /matches the bizfile/,
        /matches the source of truth/,
        /same value was detected/,
        /same values? (?:was|were) detected/,
        /was detected in this document/,
        /were detected in this document/,
        /appears consistent/,
        /appears correct/,
        /appears to be correct/,
        /no issue detected/,
        /no obvious .* issue was detected/,
        /no .* mismatch was detected/,
        /no .* inconsistency was detected/,
        /no .* problem was detected/,
        /no hidden-row issue/,
        /no amendment-style findings/
    ]

    return positivePatterns.some(pattern => pattern.test(value))
}

function formatCompactFindingLocation(location, preview = '') {
    const normalizedLocation = capitalizeSentenceStart(
        String(location || '').replace(/\s+/g, ' ').trim().replace(/[.]$/, '')
    )
    if (!normalizedLocation) return ''
    if (!preview || !/\b(?:PDF page|page)\s+\d+\b/i.test(normalizedLocation)) return normalizedLocation

    if (looksLikeCompactHeading(preview)) {
        return `${normalizedLocation}, heading`
    }

    const paragraphStart = compactLocationPreview(preview)
    return paragraphStart
        ? `${normalizedLocation}, paragraph beginning “${paragraphStart}”`
        : normalizedLocation
}

function looksLikeCompactHeading(value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim()
    if (!text) return false
    if (/\b(?:REGISTRATION\s+NO|COMPANY\s+REGISTRATION\s+NO|UEN)\b/i.test(text)) return true
    if (/^(?:CONTENTS|REGISTERED OFFICE|GENERAL INFORMATION|DIRECTORS?'? STATEMENT)\b/i.test(text)) return true

    const letters = text.replace(/[^A-Za-z]/g, '')
    return text.length <= 140 && letters.length >= 4 && text === text.toUpperCase()
}

function compactLocationPreview(value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim()
    if (!text) return ''
    if (text.length <= 58) return text
    return `${text.slice(0, 57).trim()}…`
}

function capitalizeSentenceStart(value) {
    const text = String(value || '').trim()
    if (!text) return ''
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`
}
